import React, { useState, useEffect } from 'react';
import OptimizedImage from './utils/OptimizedImage';
import ReviewsModal from './ReviewsModal';

const VendorCard = ({ vendor, onViewReviews, onReviewAdded }) => {
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [vendorRatings, setVendorRatings] = useState(vendor.ratings || { average: 0, count: 0 });

  // Handle rating update when a new review is added
  const handleReviewAdded = (updatedVendor) => {
    console.log('📊 Review added - updating ratings:', updatedVendor?.ratings);
    
    if (updatedVendor && updatedVendor.ratings) {
      setVendorRatings(updatedVendor.ratings);
    } else {
      // If no updated vendor data, increment count and recalculate average manually
      const newCount = (vendorRatings.count || 0) + 1;
      // Note: This is a simple increment. The actual average will be updated when the page refreshes
      // or when the backend provides the correct updated vendor data
      setVendorRatings(prev => ({
        ...prev,
        count: newCount
      }));
    }
    
    // Call the parent's onReviewAdded if it exists
    if (onReviewAdded) {
      onReviewAdded(updatedVendor);
    }
  };
  // Debug logging
  console.log('🏢 VendorCard received vendor:', {
    businessName: vendor.businessName,
    images: vendor.images,
    businessImageUrl: vendor.images?.businessImage?.url,
    ratings: vendor.ratings
  });

  // Update local ratings when vendor prop changes
  useEffect(() => {
    if (vendor.ratings) {
      setVendorRatings(vendor.ratings);
    }
  }, [vendor.ratings]);



  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      {/* Horizontal layout: Image on left, content on right */}
      <div className="flex flex-col md:flex-row">
        {/* Image section - Left side */}
        <div className="w-full md:w-1/3 h-48 md:h-auto">
          <OptimizedImage
            src={vendor.images?.businessImage?.url || vendor.images?.coverImage?.url || vendor.images?.coverImage || vendor.image || vendor.businessImage || 'https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=300&fit=crop'}
            alt={vendor.name || vendor.businessName}
            width={400}
            height={320}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Details section - Right side */}
        <div className="flex-1 p-6">
          {/* 1st: Business Name */}
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-xl font-semibold text-gray-800">
              {vendor.businessName || vendor.name}
            </h3>
          </div>
          
          {/* 2nd: Location and Experience */}
          <div className="mb-3">
            <p className="text-gray-600 mb-1">
              📍 {vendor.location?.city || vendor.location || 'Location not specified'}
            </p>
            <p className="text-gray-600">
              🎓 {vendor.businessInfo?.experience || vendor.experience || 'Experience not specified'} experience
            </p>
          </div>
          
          {/* 3rd: Contact Details */}
          <div className="mb-3">
            {vendor.contact?.phone && (
              <p className="text-gray-600 mb-1">
                <a href={`tel:${vendor.contact.phone}`} className="flex items-center space-x-2 hover:underline hover:text-maroon transition-colors">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span className="text-sm font-semibold">📞 {vendor.contact.phone} - Click to Call</span>
                </a>
              </p>
            )}
            {vendor.contact?.email && (
              <p className="text-gray-600 mb-1">
                ✉️ {vendor.contact.email}
              </p>
            )}
            {vendor.contact?.website && (
              <p className="text-gray-600">
                🌐 <a href={vendor.contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {vendor.contact.website}
                </a>
              </p>
            )}
          </div>
          

          
          {/* 5th: Description */}
          <div className="mb-4">
            <p className="text-gray-700 text-sm leading-relaxed">
              {vendor.description || 'No description available'}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setShowReviewsModal(true)}
              className="flex-1 bg-maroon text-white py-2 px-4 rounded-lg hover:bg-red-900 transition-colors"
            >
              View Reviews
            </button>
            <button 
              onClick={() => setShowReviewsModal(true)}
              className="flex-1 border border-maroon text-maroon py-2 px-4 rounded-lg hover:bg-maroon hover:text-white transition-colors"
            >
              Write a Review
            </button>
          </div>

          {/* Review Disclaimer */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 text-center">
              Reviews reflect individual user experiences and do not represent Ceremonzy's views
            </p>
          </div>
        </div>
      </div>
      
      {/* Reviews Modal */}
      {showReviewsModal && (
        <ReviewsModal
          vendor={vendor}
          isOpen={showReviewsModal}
          onClose={() => setShowReviewsModal(false)}
          onReviewAdded={handleReviewAdded}
        />
      )}
    </div>
  );
};

// Write Review Tab Component
const WriteReviewTab = ({ vendor, onReviewAdded, onClose }) => {
  const [newReview, setNewReview] = useState({
    comment: '',
    reviewerName: '',
    reviewerEmail: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!newReview.comment || !newReview.reviewerName || !newReview.reviewerEmail) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      
      // Import api service dynamically
      const apiService = (await import('../services/api')).default;
      
      const reviewData = {
        vendorSlug: vendor.slug || vendor.id,
        comment: newReview.comment.trim(),
        reviewerName: newReview.reviewerName.trim(),
        reviewerEmail: newReview.reviewerEmail.trim()
      };

      const response = await apiService.post('/reviews', reviewData);
      
      if (response.success) {
        // Reset form
        setNewReview({
          comment: '',
          reviewerName: '',
          reviewerEmail: ''
        });
        
        // Trigger callback to update vendor
        if (onReviewAdded) {
          onReviewAdded(response.updatedVendor || vendor);
        }
        
        alert('Review submitted successfully!');
        onClose();
      } else {
        alert(response.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Write a Review</h4>
      <form onSubmit={handleSubmitReview} className="space-y-4">


        {/* Name and Email */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name *
            </label>
            <input
              type="text"
              value={newReview.reviewerName}
              onChange={(e) => setNewReview(prev => ({ ...prev, reviewerName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Email *
            </label>
            <input
              type="email"
              value={newReview.reviewerEmail}
              onChange={(e) => setNewReview(prev => ({ ...prev, reviewerEmail: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon text-sm"
              required
            />
          </div>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Review *
          </label>
          <textarea
            value={newReview.comment}
            onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon text-sm"
            placeholder="Share your experience..."
            required
          />
        </div>

        {/* Submit Button */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-maroon text-white py-2 px-4 rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// View Reviews Tab Component
const ViewReviewsTab = ({ vendor, onViewAllReviews, vendorRatings }) => {
  const [recentReviews, setRecentReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentReviews = async () => {
      try {
        setLoading(true);
        // Import api service dynamically
        const apiService = (await import('../services/api')).default;
        
        // Fetch recent reviews (limit to 3)
        const response = await apiService.get(`/reviews/vendor/${vendor.slug || vendor.id}?limit=3`);
        
        if (response.success) {
          setRecentReviews(response.reviews || []);
        } else {
          setRecentReviews([]);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setRecentReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentReviews();
  }, [vendor.slug, vendor.id]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="text-gray-600">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-semibold text-gray-800">Recent Reviews</h4>
        {recentReviews.length > 0 && (
          <button
            onClick={onViewAllReviews}
            className="text-sm text-maroon hover:text-red-900 underline"
          >
            View All Reviews
          </button>
        )}
      </div>

      {recentReviews.length > 0 ? (
        <div className="space-y-4">
          {recentReviews.map((review, index) => (
            <div key={review._id || index} className="border-b border-gray-200 pb-4 last:border-b-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">
                    {review.reviewerName}
                  </div>
                  <div className="flex items-center gap-2 mt-1">

                    <span className="text-xs text-gray-500">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm">{review.comment}</p>
            </div>
          ))}
          
          {/* View All Reviews Button */}
          <div className="pt-2">
            <button
              onClick={onViewAllReviews}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              View All {vendorRatings?.count || 0} Reviews
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-600">
          <p className="text-sm">No reviews yet.</p>
          <p className="text-xs text-gray-500 mt-1">Be the first to leave a review!</p>
        </div>
      )}
    </div>
  );
};

export default VendorCard;